import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Joi from 'joi';
import { TypographyType, TypographyVariant } from 'src/components-dummy';
import { Types, passwordV2, ConfirmPassword } from 'src/components-bl';
import { resetPasswordActions } from '../Actions';
import { IResetPasswordForm } from '../types';
import { useValidateSchema } from '../../../hooks';
import {
  ActionButtonStyled,
  TitleTypographyStyled,
  InputActionTextStyled,
  LinkTypographyStyled,
} from '../../Login/Login.styles';

const schema = {
  newPassword: passwordV2.schema.required(),
  confirmNewPassword: passwordV2.schema
    .valid(Joi.ref('newPassword'))
    .messages({ 'any.only': "Passwords don't match" }),
  token: Joi.string().guid().required(),
};

const tokenKey = 'resetToken';

interface ResetPasswordFormProps {
  onSuccess: () => void;
  goToLogin: () => void;
}

export const ResetPasswordForm = ({
  onSuccess,
  goToLogin,
}: ResetPasswordFormProps): JSX.Element => {
  const token = new URLSearchParams(useLocation().search).get(tokenKey) || '';

  const initialState: IResetPasswordForm = {
    newPassword: '',
    confirmNewPassword: '',
    token,
  };

  const dispatch: Types.Dispatch = useDispatch();
  const [state, setState] = useState<IResetPasswordForm>(initialState);

  const {
    errors,
    validate,
    isValid: formIsValid,
    validatePartial,
  } = useValidateSchema<IResetPasswordForm>({
    schema,
    initialData: state,
    validateOnStart: false,
  });

  const onChange = (args: Partial<IResetPasswordForm>) => {
    const updatedState = { ...state, ...args };
    setState(updatedState);
    validate({ dataToValidate: updatedState });
  };

  const onSubmit = () => {
    if (formIsValid && state.newPassword) {
      (
        dispatch(
          resetPasswordActions.submit({ newPassword: state.newPassword, resetToken: token })
        ) as any
      )
        .unwrap()
        .then(() => {
          onSuccess();
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    validatePartial({ dataToValidate: { token: state.token } });
  }, [state.token]);

  useEffect(() => {
    if (errors.token) {
      dispatch(resetPasswordActions.invalidToken());
      goToLogin();
    }
  }, [errors.token]);

  return (
    <>
      <TitleTypographyStyled type={TypographyType.Heading} variant={TypographyVariant.SmallBold}>
        Enter new password
      </TitleTypographyStyled>
      <ConfirmPassword
        state={state}
        onChange={onChange}
        errors={errors}
        requireOldPassword={false}
      />
      <ActionButtonStyled onClick={onSubmit} variant='primary' disabled={!formIsValid}>
        Set new password
      </ActionButtonStyled>
      <InputActionTextStyled onClick={goToLogin} align='center'>
        <LinkTypographyStyled type={TypographyType.Body} variant={TypographyVariant.MediumRegular}>
          Back to login
        </LinkTypographyStyled>
      </InputActionTextStyled>
    </>
  );
};
